/**
 * Guest list — README §1 GUESTS `VIEW ALL N GUESTS`, on the Portal vocabulary (Oct 20 2026, the
 * same surface as the share sheet: a 28px-radius white card, no borders anywhere, #F2F2F2 fills
 * and pills, round avatars). Desktop: centred card (max-width 620, sticky header + search,
 * internal scroll). Mobile: `BottomSheet`. Rows: round avatar (→ /{handle}), name, FRIEND tag,
 * sub line, `+ ADD FRIEND` pill → `REQUESTED` / `FRIENDS` / `NO ACCOUNT`.
 *
 * Data: `eventService.getGuestList` (+ `friendService.getFriends` when `viewerFriendIds` is
 * not supplied), including the viewer's own visibility switch (`setGuestListVisibility`) as
 * the first row when the viewer is on the list.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { eventService, friendService } from '../../services';
import { BottomSheet, SwitchRow, EmptyBox, MUTED, SUBTLE } from '../surface';
import { RING } from '../surface/SurfaceStyleContext';
import { RoundAvatar } from './EventHeader';
import { plural } from '../../utils/surfaceFormat';
import { CloseFriendMark, MirrorRoom, useCloseFriend } from '../closefriends';

const FILL = '#F2F2F2';
const PILL = 'flex-shrink-0 whitespace-nowrap rounded-full font-mono font-bold uppercase';
const pillStyle = { fontSize: 9, letterSpacing: '0.14em', height: 36, padding: '0 14px', display: 'inline-flex', alignItems: 'center' };

function rowLabel(entry) {
  if (entry.isAnonymous) return 'HIDDEN';
  if (!entry.userId) return 'NO ACCOUNT';
  return null;
}

/**
 * `closeFriend` is set only for confirmed friends (true/false = marked close
 * or not); undefined means no close-friends mark. It isn't derived from
 * isFriend, which also turns on the moment a request is sent.
 */
export function GuestRow({ entry, isFriend, requested, onAddFriend, mobile, closeFriend }) {
  const hasMark = closeFriend !== undefined && !entry.isViewer && !entry.isAnonymous && !!entry.userId;
  const close = useCloseFriend(entry.userId, !!closeFriend, entry.displayName);
  const markRef = useRef(null);
  // An opted-out guest reaches other guests as a bare "Anonymous" row; the host, co-hosts and
  // staff get the name (the server keeps the identity for them, still tagged anonymous → HIDDEN).
  const faceless = entry.isAnonymous && !entry.displayName;
  const name = faceless ? `Anonymous${entry.isViewer ? ' (you)' : ''}` : `${entry.displayName || 'Guest'}${entry.isViewer ? ' (you)' : ''}`;
  const sub = entry.isAnonymous ? (faceless ? 'Hidden by choice' : 'Hidden from other guests') : entry.handle ? `@${entry.handle}` : entry.userId ? '' : 'Plus-one';
  const label = rowLabel(entry);
  // A guest-checkout stub is a name on the list and nothing else: no page, no friend request.
  const canAdd = !entry.isViewer && !!entry.userId && !entry.isAnonymous && !entry.isStub;
  const size = mobile ? 38 : 44;
  const avatar = faceless
    ? <span className="flex flex-shrink-0 items-center justify-center rounded-full font-mono font-bold" style={{ width: size, height: size, background: FILL, color: MUTED, fontSize: 15 }}>?</span>
    : <RoundAvatar person={{ displayName: entry.displayName || '?', avatarUrl: entry.avatarUrl, handle: entry.handle, userId: entry.userId }} size={size} />;
  // Every real account has a Portal Page: `/{handle}`, or `/users/{userId}` for the third of
  // accounts that never picked a handle.
  const href = faceless || entry.isStub ? null : entry.handle ? `/${entry.handle}` : entry.userId ? `/users/${entry.userId}` : null;
  return (
    <div className="relative flex items-center gap-3 font-mono" style={{ padding: mobile ? '7px 0' : '8px 0' }} data-testid={`guest-row-${entry.userId || 'anon'}`}>
      {href ? <a href={href} className="flex-shrink-0 rounded-full" aria-label={entry.displayName || entry.handle}>{avatar}</a> : avatar}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {href
            ? <a href={href} className="truncate font-bold hover:underline" style={{ fontSize: mobile ? 12 : 13 }}>{name}</a>
            : <span className="truncate font-bold" style={{ fontSize: mobile ? 12 : 13, color: entry.isAnonymous ? MUTED : '#000' }}>{name}</span>}
          {isFriend && <span className="rounded-full font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.14em', padding: '3px 8px', background: FILL, color: MUTED }}>FRIEND</span>}
        </div>
        {close.error
          ? <div role="alert" className="truncate font-bold uppercase" style={{ fontSize: mobile ? 10 : 11, marginTop: 2 }}>{close.error}</div>
          : sub && <div className="truncate" style={{ fontSize: mobile ? 10 : 11, color: MUTED, marginTop: 2 }}>{sub}</div>}
      </div>
      {canAdd ? (
        isFriend ? (
          <span className={PILL} style={{ ...pillStyle, background: FILL, color: MUTED }}>FRIENDS</span>
        ) : requested ? (
          <span className={PILL} style={{ ...pillStyle, background: FILL, color: MUTED }} data-testid={`guest-requested-${entry.userId}`}>REQUESTED</span>
        ) : (
          <button
            type="button"
            onClick={() => onAddFriend(entry.userId)}
            className={`${PILL} bg-black text-white transition-colors duration-200 hover:bg-[#00FFFF] hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2`}
            style={{ ...pillStyle, border: 'none' }}
            data-testid={`guest-add-${entry.userId}`}
          >
            + ADD FRIEND
          </button>
        )
      ) : label ? (
        <span className="flex-shrink-0 font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: SUBTLE }}>{label}</span>
      ) : null}
      {hasMark && (
        <>
          <CloseFriendMark markRef={markRef} size={mobile ? 44 : 32} on={close.on} onToggle={close.toggle} name={entry.displayName} />
          <MirrorRoom q={close.q} surface="guest" first={close.first} anchorRef={markRef} />
        </>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

export default function EventGuestListModal({ open, onClose, event, canView = true, mobile = false, viewerFriendIds = null, viewerCloseMap = null, onAddFriend }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [friendIds, setFriendIds] = useState(() => new Set(viewerFriendIds || []));
  const [reqStatus, setReqStatus] = useState({});
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => { if (viewerFriendIds) setFriendIds(new Set(viewerFriendIds)); }, [viewerFriendIds]);
  // Confirmed friends → isClose, for the close-friends mark on their rows.
  const [closeMap, setCloseMap] = useState(() => viewerCloseMap || new Map());
  useEffect(() => { if (viewerCloseMap) setCloseMap(viewerCloseMap); }, [viewerCloseMap]);

  useEffect(() => {
    if (!open || !canView || !event?.id) return undefined;
    let cancelled = false;
    setLoaded(false);
    (async () => {
      const res = await eventService.getGuestList(event.id);
      if (cancelled) return;
      if (res.success) setData(res.data);
      setLoaded(true);
      if (!viewerFriendIds) {
        const fr = await friendService.getFriends();
        if (!cancelled && fr.success) {
          setFriendIds(new Set((fr.data || []).map((f) => f.userId)));
          setCloseMap(new Map((fr.data || []).map((f) => [f.userId, !!f.isClose])));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, canView, event?.id, viewerFriendIds]);

  const addFriend = async (userId) => {
    setReqStatus((s) => ({ ...s, [userId]: 'requested' }));
    const r = await friendService.sendFriendRequest(userId);
    if (!r.success) setReqStatus((s) => ({ ...s, [userId]: 'error' }));
    else onAddFriend?.(userId);
  };

  const toggleVisibility = async (show) => {
    setSaving(true);
    try {
      const res = await eventService.setGuestListVisibility(event.id, show);
      if (res.success) {
        const refreshed = await eventService.getGuestList(event.id);
        if (refreshed.success) setData(refreshed.data);
      }
    } finally {
      setSaving(false);
    }
  };

  const entries = useMemo(() => {
    const list = data?.entries || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => (e.displayName || '').toLowerCase().includes(q) || (e.handle || '').toLowerCase().includes(q));
  }, [data, query]);

  const going = data ? data.shownCount + data.anonymousCount : Number(event?.acceptedCount || 0);
  const note = !canView ? 'SIGN IN TO SEE WHO' : `${going} GOING`;
  const side = mobile ? 18 : 26;

  const content = (
    <>
      {canView && (
        <div className="flex-none" style={{ padding: mobile ? '6px 18px 10px' : '4px 26px 12px' }}>
          <label
            className={`flex items-center gap-2.5 rounded-full ${searchFocused ? 'pr-ring-anim' : ''}`}
            // Focus is the moving rainbow ring around the whole pill (the Portal focus state), not
            // the browser's outline on the input inside it.
            style={{ height: 44, padding: '0 14px', color: MUTED, border: '2px solid transparent', background: searchFocused ? `linear-gradient(${FILL},${FILL}) padding-box, ${RING} border-box` : FILL }}
            data-testid="guest-search-pill"
            data-focused={searchFocused ? 'true' : undefined}
          >
            <SearchIcon />
            <input
              type="search"
              value={query}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this list…"
              aria-label="Search this list"
              autoComplete="off"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-black shadow-none outline-none ring-0 placeholder:text-[#9CA3AF] focus:border-0 focus:ring-0"
              style={{ fontSize: 13, border: 'none' }}
              data-testid="guest-search"
            />
          </label>
        </div>
      )}
      <div className="min-h-0 overflow-y-auto" style={{ overscrollBehavior: 'contain', padding: `0 ${side}px ${mobile ? 18 : 22}px` }} data-testid="guest-list-scroll">
        {!canView ? (
          <EmptyBox title="GUESTS ONLY." mobile={mobile}>Sign in and get a ticket to see who is coming.</EmptyBox>
        ) : !loaded ? (
          <div className="font-mono uppercase" style={{ padding: '18px 0', fontSize: 11, letterSpacing: '0.14em', color: SUBTLE }}>LOADING…</div>
        ) : (
          <>
            {data?.viewerShown != null && (
              <div style={{ background: FILL, borderRadius: 18, padding: '2px 16px', marginBottom: 8 }} data-testid="guest-visibility-row">
                <SwitchRow label="Show me on the guest list" desc="Off keeps your name out of it; you still count." checked={!!data.viewerShown} onChange={toggleVisibility} disabled={saving} dense />
              </div>
            )}
            {entries.length === 0 ? (
              <EmptyBox title={query ? 'NOBODY BY THAT NAME.' : 'NOBODY YET.'} mobile={mobile} />
            ) : entries.map((entry, i) => (
              <GuestRow
                key={entry.userId || `anon-${i}`}
                entry={entry}
                mobile={mobile}
                isFriend={!!entry.userId && friendIds.has(entry.userId)}
                requested={reqStatus[entry.userId] === 'requested'}
                onAddFriend={addFriend}
                closeFriend={entry.userId && closeMap.has(entry.userId) ? closeMap.get(entry.userId) : undefined}
              />
            ))}
          </>
        )}
      </div>
    </>
  );

  if (mobile) {
    return (
      <BottomSheet open={open} onClose={onClose} title="WHO'S IN" note={note} testId="guest-list-sheet">
        <div className="flex min-h-0 flex-col">{content}</div>
      </BottomSheet>
    );
  }

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div className="pr-overlay-root fixed inset-0 safe-inset z-[80] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', padding: '48px 24px' }} onClick={onClose} data-testid="guest-list-modal-scrim">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="WHO'S IN"
        className="flex w-full flex-col overflow-hidden bg-white text-black"
        style={{ maxWidth: 620, maxHeight: '100%', borderRadius: 28 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="guest-list-modal"
      >
        <div className="flex flex-none items-start justify-between gap-4" style={{ padding: '22px 26px 14px' }}>
          <div className="min-w-0">
            <div className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.16em' }}>WHO'S IN</div>
            <div className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: SUBTLE, marginTop: 4 }}>{note}{canView && data && data.anonymousCount > 0 ? ` · ${plural(data.anonymousCount, 'HIDDEN', 'HIDDEN')}` : ''}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex flex-none items-center justify-center rounded-full font-mono font-bold transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2" style={{ width: 38, height: 38, fontSize: 15, background: FILL }}>✕</button>
        </div>
        {content}
      </div>
    </div>,
    document.body,
  );
}
